import { useEffect, useState, useCallback, useRef } from 'react'
import {
  queryWorkspaceActivityPage,
  type WorkspaceActivityRecord,
} from '../../../../services/activityService'
import { firestoreDb } from '../../../../services/firebase'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import { timeRangeToDateRange } from './bucketize'

export type UseAuditEventsResult = {
  records: WorkspaceActivityRecord[]
  loading: boolean
  error: Error | null
  refetch: () => void
}

export const useAuditEvents = ({
  workspaceId,
  timeRange,
  refreshKey = 0,
  pageSize = 500,
}: {
  workspaceId: string
  timeRange: AnalysisTimeRangeKey
  /**
   * External refresh signal (e.g. AnalysisHub's snapshotNonce).
   * Bumping it re-fires the query even if workspaceId / timeRange haven't changed.
   * The underlying service does not offer live Firestore subscriptions; this is
   * the pragmatic freshness path.
   */
  refreshKey?: number
  pageSize?: number
}): UseAuditEventsResult => {
  const [records, setRecords] = useState<WorkspaceActivityRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reloadNonce, setReloadNonce] = useState(0)

  const refetch = useCallback(() => {
    setReloadNonce((count) => count + 1)
  }, [])

  const activeRequest = useRef(0)

  useEffect(() => {
    if (!workspaceId) {
      setRecords([])
      setLoading(false)
      setError(null)
      return undefined
    }

    const requestId = ++activeRequest.current
    setLoading(true)
    setError(null)

    const { from, to } = timeRangeToDateRange(timeRange)

    void queryWorkspaceActivityPage(
      firestoreDb,
      workspaceId,
      {
        from,
        to,
        memberIds: [],
        itemIds: [],
        activityTypes: [],
      },
      null,
      pageSize,
    )
      .then((result) => {
        if (requestId === activeRequest.current) {
          setRecords(result.records)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (requestId === activeRequest.current) {
          setError(err instanceof Error ? err : new Error('Failed to load workspace activity'))
          setRecords([])
          setLoading(false)
        }
      })

    return () => {
      activeRequest.current += 1
    }
  }, [workspaceId, timeRange, pageSize, reloadNonce, refreshKey])

  return { records, loading, error, refetch }
}
